import { buildArtifactDisplayModel } from './chatArtifactDisplayModel';
import { leadContactsBelongToLaterTurn } from './chatArtifactLeadModel';
import { isTerminalJobStatus as isTerminalChatJobStatus } from './chatJobPolling';
import {errorDetail} from '../../helper/errorDetail';
import { isExportSuggestion } from './chatArtifactExportModel';
import { ownerOfferActions, restoredOfferPatch, withOfferQuote } from './chatEnrichmentOfferModel';
import { ChatConnectionError, isNetworkFailureResponse } from './chatConnectionError';
import {ownerProgressPatch} from './ownerProgressState';

function newId() {
  return Math.random().toString(36).slice(2, 10);
}

export function newMsg(role, content) {
  return {
    id: newId(),
    role,
    content,
    notice: null,
    companies: [],
    createdAt: new Date().toISOString(),
    jobId: null,
    jobStatus: null,
    artifactId: null,
    referencedArtifactId: null,
    artifactDisplay: null,
    artifactAnswer: null,
    artifactAnswerActions: [],
    suggestions: [],
    answerConfirmed: false,
    clarificationOptions: [],
    refinementSummary: null,
    exportError: null,
    exportWarning: null,
    actionErrors: {},
    actionNotices: {},
  };
}

function firstPresent(...values) {
  return values.find((value) => value !== undefined && value !== null && value !== '');
}

export function artifactMessageContent(artifactDisplay, fallback = '') {
  if (artifactDisplay?.coreResultSet) return '';
  return firstPresent(artifactDisplay?.summary, fallback, '');
}

// FE #762: a turn that proposes an action over the list it built (named companies)
// says what it found and what it offers; the card summary must not replace that.
function turnContent(artifactDisplay, data, text) {
  return (data?.artifact_action_proposal || isArtifactActionTurn(data)) && text
    ? text : artifactMessageContent(artifactDisplay, text);
}

export function responseMessage(data, fallback) {
  const detailMessage = errorDetail(data?.detail, '');
  const message = firstPresent(data?.message, detailMessage, data?.status_message, data?.status_reason, fallback);
  return typeof message === 'string' ? message : '';
}

export function deferredSourceNotice(data) {
  const sources = Array.isArray(data?.deferred_sources) ? data.deferred_sources : [];
  if (data?.status === 'accepted' && sources.length > 0) {
    return responseMessage(data, null);
  }
  return null;
}

function artifactContextLabel(message) {
  const title = message?.artifactDisplay?.title;
  if (title && title !== 'Target list results') return title;
  return 'the current saved target list';
}

export function latestArtifactContext(messages) {
  const message = [...messages]
    .reverse()
    .find((item) => item?.artifactDisplay?.artifactId || item?.artifactId);
  if (!message) return null;
  return {
    artifactId: message.artifactDisplay?.artifactId || message.artifactId,
    label: artifactContextLabel(message),
  };
}

function isArtifactClarification(data) {
  const message = responseMessage(data, '').toLowerCase();
  return (
    message.includes('saved target list') ||
    message.includes('saved candidates') ||
    message.includes('current saved')
  );
}

export function followUpContextNotice(data, artifactContext) {
  if (!artifactContext?.artifactId) return null;
  if (data?.status === 'artifact_answer') {
    return `Answered from ${artifactContext.label}.`;
  }
  if (data?.status === 'clarification' && isArtifactClarification(data)) {
    return `Checked ${artifactContext.label}.`;
  }
  return null;
}

export function ensureOkResponse(response, fallback) {
  if (isNetworkFailureResponse(response)) {
    throw new ChatConnectionError();
  }
  if (!response || response.status >= 400) {
    throw new Error(responseMessage(response?.data, fallback));
  }
  return response.data || {};
}

function formatLocation(location) {
  const parts = [
    firstPresent(location?.locality, location?.city, location?.hq_city),
    firstPresent(location?.region, location?.state, location?.region1, location?.hq_state),
    firstPresent(location?.country, location?.country_code),
  ].filter(Boolean);

  return parts.join(', ');
}

function formatHeadcount(candidate, company) {
  const headcount = firstPresent(
    candidate?.headcount,
    candidate?.employee_count,
    company?.headcount,
    company?.employee_count,
    company?.employees
  );

  if (!headcount) return undefined;
  if (typeof headcount === 'number') return `${headcount.toLocaleString()} employees`;
  return String(headcount);
}

function formatEvidence(evidence) {
  if (!Array.isArray(evidence)) return undefined;
  const values = evidence
    .map((item) => {
      if (typeof item === 'string') return item;
      return firstPresent(item?.text, item?.label, item?.reason);
    })
    .filter(Boolean);
  return values.length > 0 ? values.join(' | ') : undefined;
}

export function candidateToCompany(candidate) {
  const metadata = candidate?.metadata || {};
  const company = candidate?.company || candidate?.company_snapshot || metadata || {};
  const location =
    metadata.primary_location ||
    metadata.primaryLocation ||
    company.primary_location ||
    company.primaryLocation ||
    company.location ||
    company;

  return {
    id: firstPresent(candidate?.company_id, candidate?.id, candidate?._id),
    name: firstPresent(candidate?.company_name, company?.name, company?.company_name, 'Unknown company'),
    industry: firstPresent(candidate?.industry, metadata?.industry, company?.industry, company?.sector),
    hq: formatLocation(location),
    headcount: formatHeadcount(candidate, company),
    description: firstPresent(formatEvidence(candidate?.evidence), company?.description),
    enrichmentColumns: candidate?.enrichment_columns || {},
  };
}

export function discoveryCompleteMessage(companies) {
  const count = companies.length;
  if (count === 1) return 'Found 1 company.';
  return `Found ${count} companies.`;
}

// The saved list's size, not its five-row sample: a Core card counts the frozen
// scope's complete rows, a legacy card its total (sweep D4).
export function artifactResultCount(artifactDisplay) {
  if (!artifactDisplay) return undefined;
  return artifactDisplay.coreResultSet
    ? artifactDisplay.coreResultSet.completeRowCount
    : artifactDisplay.totalCount;
}

export function latestCompanyCount(messages) {
  const lastResult = [...messages]
    .reverse()
    .find((message) => message.role === 'assistant' && message.companies?.length > 0);
  return artifactResultCount(lastResult?.artifactDisplay) ?? (lastResult?.companies.length || 0);
}

export function refinementSummary(previousCount, currentCount) {
  const noun = currentCount === 1 ? 'company' : 'companies';
  return `Refined your list: ${previousCount.toLocaleString()} -> ${currentCount.toLocaleString()} ${noun}`;
}

// A turn about the current list (artifact question answer, clarification, action
// proposal, enrichment offer) echoes the artifact it *references* as artifact_id,
// without the artifact payload or candidates. An enrichment offer also carries the
// job it would start, so a job_id is not ownership either (FE #522). Rendering a
// result card from the id alone shows an empty "Total results: 0 companies" table,
// which reads as a failed new search.
function ownsArtifactPayload(data) {
  return Boolean(
    data?.artifact ||
      data?.core_result_set ||
      (Array.isArray(data?.candidates) && data.candidates.length > 0)
  );
}

function artifactIdentity(data) {
  const id = data?.artifact_id || null;
  if (!id) return { artifactId: null, referencedArtifactId: null };
  return ownsArtifactPayload(data)
    ? { artifactId: id, referencedArtifactId: null }
    : { artifactId: null, referencedArtifactId: id };
}

function turnSuggestions(data, artifactId) {
  const suggestions = suggestionsFromPayload(data);
  return artifactId ? suggestions : suggestions.filter((item) => !isExportSuggestion(item));
}

export function historyMessageToMessage(message, index, history) {
  const candidates = Array.isArray(message?.candidates) ? message.candidates : [];
  const companies = candidates.map(candidateToCompany);
  const { artifactId, referencedArtifactId } = artifactIdentity(message);
  const artifactDisplay = artifactId
    ? buildArtifactDisplayModel({
        artifactData: {
          artifact: message?.artifact || { artifact_id: artifactId },
          candidates,
          ...(Object.prototype.hasOwnProperty.call(message || {}, 'core_result_set')
            ? { core_result_set: message.core_result_set }
            : {}),
          // FE #748: history carries the same display-only preview as GET artifact.
          ...(Array.isArray(message?.preview_rows) ? { preview_rows: message.preview_rows } : {}),
        },
        message,
        fallbackCompanies: companies,
      })
    : null;

  if (artifactDisplay && leadContactsBelongToLaterTurn(message, index, history)) {
    artifactDisplay.leadSummary = null;
    artifactDisplay.leadLockedHint = null;
  }
  const partialFailureNotice = postDiscoveryActionNotice(message);

  return {
    id: firstPresent(message?.message_id, message?.job_id, newId()),
    role: firstPresent(message?.role, 'assistant'),
    content: turnContent(artifactDisplay, message, message?.content),
    notice: message?.notice || null,
    companies,
    createdAt: message?.created_at,
    jobId: message?.job_id || null,
    jobType: message?.job_type || null,
    jobStatus: message?.job_status || null,
    artifactActionKey: message?.artifact_action_key || ACTION_JOB_KEYS[message?.job_type] || null,
    jobArtifactId: isArtifactActionTurn(message) ? message.artifact_id || null : null,
    artifactId,
    referencedArtifactId,
    artifactDisplay,
    artifactAnswer: message?.artifact_answer || null,
    artifactAnswerActions: ownerOfferActions(
      withOfferQuote(message?.suggested_actions, message?.artifact_action_proposal),
      message?.artifact,
      { history, index }
    ),
    suggestions: turnSuggestions(message, artifactId),
    clarificationOptions: clarificationOptionsFromPayload(message),
    refinementSummary: null,
    exportError: null,
    exportWarning: null,
    artifactActionNotice: partialFailureNotice,
    artifactActionNoticeSeverity: partialFailureNotice ? 'warning' : 'success',
    actionErrors: {},
    actionNotices: {},
    ...restoredOfferPatch(message),
    ...ownerProgressPatch(message),
  };
}

export function isTerminalJobStatus(status) {
  return isTerminalChatJobStatus(status);
}

// An offer waiting on the user's approval is not running work: polling it only
// held the whole chat busy until the approval expired (sweep D10).
export function hasActiveJob(message) {
  return Boolean(
    message.role === 'assistant' &&
      message.jobId &&
      message.jobStatus !== 'awaiting_approval' &&
      !isTerminalJobStatus(message.jobStatus)
  );
}

// Job progress never makes a turn own an artifact: a job about the current list
// echoes the artifact it references, and stamping that id here gave non-owning
// turns a dead Export CSV chip (sweep D11). The artifact-owning completion path
// sets artifactId itself.
export function progressPatchFromJob(jobData, { isActionTurn = false } = {}) {
  return { jobStatus: jobData?.status, progressLabel: runningStageLabel(jobData, isActionTurn), ...ownerProgressPatch(jobData) };
}

// The stage a running discovery is in, e.g. "Rank and filter (3 of 4)" (sweep D16).
function runningStageLabel(jobData, isActionTurn) {
  const stages = Array.isArray(jobData?.progress_stages) ? jobData.progress_stages : [];
  const index = stages.findIndex((stage) => stage?.status === 'running');
  const label = index >= 0 ? firstPresent(stages[index].label, stages[index].key) : null;
  return label ? (isActionTurn ? label : `${label} (${index + 1} of ${stages.length})`) : null;
}

export function suggestionsFromPayload(data) {
  return Array.isArray(data?.suggestions) ? data.suggestions : [];
}

// BE #874: answers the backend validated for a clarification; a click sends `value` as the next message.
export function clarificationOptionsFromPayload(data) {
  const options = Array.isArray(data?.clarification_options) ? data.clarification_options : [];
  return options.filter((option) => (
    typeof option?.label === 'string' && option.label.trim()
    && typeof option?.value === 'string' && option.value.trim()
  ));
}

export function postDiscoveryActionNotice(data) {
  const failedOutcomes = Array.isArray(data?.post_discovery_action_outcomes)
    ? data.post_discovery_action_outcomes.filter((outcome) => outcome?.status === 'failed')
    : [];
  if (failedOutcomes.length === 0) return null;

  const messages = failedOutcomes
    .map((outcome) => responseMessage(outcome, null))
    .filter(Boolean);
  return messages.length > 0
    ? messages.join(' ')
    : 'A requested follow-up action could not be completed.';
}

export function assistantPatchFromSendPayload(messageData) {
  const { job_id: jobId = null, status } = messageData || {};
  const candidates = Array.isArray(messageData?.candidates) ? messageData.candidates : [];
  const companies = candidates.map(candidateToCompany);
  const { artifactId, referencedArtifactId } = artifactIdentity(messageData);
  const artifactDisplay = artifactId
    ? buildArtifactDisplayModel({
        artifactData: {
          artifact: messageData?.artifact || { artifact_id: artifactId },
          candidates,
        },
        message: {
          artifactId,
          content: messageData?.message,
          artifact: messageData?.artifact,
          candidates,
        },
        fallbackCompanies: companies,
      })
    : null;

  return {
    answerConfirmed: status !== 'processing' && !jobId,
    content: turnContent(artifactDisplay, messageData, responseMessage(messageData, 'Discovery started.')),
    companies,
    artifactId,
    referencedArtifactId,
    artifactDisplay,
    artifactAnswer: messageData?.artifact_answer || null,
    artifactAnswerActions: withOfferQuote(messageData?.suggested_actions, messageData?.artifact_action_proposal),
    suggestions: turnSuggestions(messageData, artifactId),
    clarificationOptions: clarificationOptionsFromPayload(messageData),
    // Clear the pending marker on direct replies; a named job owns its own clock.
    jobId,
    jobStatus: jobId || status === 'processing' ? status : null,
  };
}

export function referencedArtifactDisplay(messages, artifactId) {
  if (!artifactId) return null;
  const message = [...(messages || [])]
    .reverse()
    .find((item) => item?.artifactDisplay?.artifactId === artifactId);
  return message?.artifactDisplay || null;
}

// Free sample jobs deliberately keep their original in-place discovery renderer.
const ACTION_JOB_KEYS = {
  artifact_lead_enrichment: 'lead_finder_contacts',
  artifact_answer_enrichment: 'answer_by_enriching',
  artifact_strategic_enrichment: 'enhance_company_data',
};
const ACTION_JOB_TYPES = new Set([...Object.keys(ACTION_JOB_KEYS), 'artifact_connector_enrichment']);

export function isArtifactActionTurn(message) {
  const type = message?.jobType || message?.job_type;
  const key = message?.artifactActionKey || message?.artifact_action_key;
  if (type === 'artifact_lead_sample_enrichment' || key === 'find_leads_sample') return false;
  return ACTION_JOB_TYPES.has(type) || Boolean(key);
}
